<script setup lang="ts">
import Faq from "~/components/ui/Faq.vue";
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  faqNode,
  breadcrumbNode,
} from "../composables/useSeo";
import { usePhoneNumber } from "../composables/usePhoneNumber";
import { useAnalytics } from "../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { pricing } = usePricing();

useSeoBase({
  title: "Pricing | Transparent Junk Removal Costs | Junk Guys MN",
  description:
    "Transparent pricing for junk removal services. Volume-based pricing with on-site confirmation. See our pricing guide and get a free estimate.",
  canonical: "https://junkguysmn.com/pricing",
});

const faqs = [
  {
    q: "How do you determine pricing?",
    a: "Our pricing is volume-based, meaning we charge based on how much space your items take up in our truck. We provide on-site pricing confirmation to ensure accuracy.",
  },
  {
    q: "Can I get an estimate before you arrive?",
    a: "Yes! You can text us photos of your items for a rough estimate. For the most accurate pricing, we'll confirm the final amount on-site.",
  },
  {
    q: "Are there any hidden fees?",
    a: "No hidden fees! Our quoted price includes pickup, loading, transportation, and disposal. What we quote is what you pay.",
  },
  {
    q: "Do you offer same-day service?",
    a: "Yes, we often have same-day availability! It's best to call early in the day to secure a spot. Availability depends on our current schedule and your location.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept cash, credit cards, Zelle, Venmo, and Cash App. Payment is due upon completion of the service.",
  },
];

useJsonLd([
  orgNode(),
  localBusinessNode(),
  faqNode(faqs),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Pricing", url: "https://junkguysmn.com/pricing" },
  ]),
]);

const pricingTiers = computed(() => pricing.value.generalLoad);
const specialtyItems = computed(() => pricing.value.specialtyItems);
const demolitionServices = computed(() => pricing.value.demolitionServices);

const boxIconCount = (tierId: string) => {
  const countByTier: Record<string, number> = {
    "minimum-pick-up": 1,
    "quarter-trailer": 1,
    "half-trailer": 2,
    "three-quarter-trailer": 3,
    "full-trailer": 4,
  };

  return countByTier[tierId] ?? 1;
};
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Transparent Pricing
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Volume-based pricing with no hidden fees. Get a free estimate and
            transparent pricing for your junk removal needs.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Get Free Estimate
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Disclaimer -->
    <section class="bg-amber-50 border-b border-amber-200">
      <div class="mx-auto max-w-4xl px-4 py-6">
        <div class="flex items-start gap-3">
          <div class="text-amber-600 text-xl">⚠️</div>
          <div class="text-amber-800">
            <p class="font-semibold mb-1">Important Pricing Information</p>
            <p class="text-sm">
              The images below are visual representations of load sizes and are
              for reference only. Actual pricing may vary based on item type,
              weight, accessibility, and disposal requirements. We provide
              on-site pricing confirmation for accuracy.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Pricing Grid -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Load-Based Pricing
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Our pricing is determined by the amount of space your junk takes up
            in our truck. See examples below and get a free estimate for your
            specific needs.
          </p>
        </div>

        <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-6">
          <div
            v-for="(tier, index) in pricingTiers"
            :key="tier.name"
            class="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 lg:col-span-2"
            :class="{ 'lg:col-start-2': index === 3 }"
          >
            <!-- Header -->
            <div class="p-6 text-center border-b border-slate-100">
              <div class="mb-3 text-4xl" :aria-label="`${tier.name} load size`">
                <span v-for="box in boxIconCount(tier.id)" :key="box">📦</span>
              </div>
              <h3 class="text-xl font-bold text-slate-900 mb-2">
                {{ tier.name }}
              </h3>
              <p class="text-sm text-slate-600">{{ tier.description }}</p>
            </div>

            <!-- Pricing -->
            <div class="p-6 text-center">
              <div class="text-3xl font-bold text-primary-main mb-2">
                {{ tier.price }}
              </div>
              <div class="text-sm text-slate-500 mb-4">
                {{ tier.truckSpace }}
              </div>

              <!-- Examples -->
              <div class="space-y-2 text-left">
                <h4 class="font-semibold text-slate-700 text-sm mb-3">
                  Examples:
                </h4>
                <div
                  v-for="example in tier.examples"
                  :key="example"
                  class="flex items-center gap-2 text-sm text-slate-600"
                >
                  <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                  {{ example }}
                </div>
              </div>
            </div>

            <!-- CTA -->
            <div class="p-6 pt-0">
              <a
                :href="phoneNumberTel"
                @click="trackConversion"
                class="w-full bg-primary-main hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-lg text-center block transition-colors"
              >
                Get Estimate
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Additional Services Pricing -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-6xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Additional Services
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Specialized services with individual pricing. Contact us for
            specific quotes.
          </p>
        </div>

        <div class="grid gap-8 lg:grid-cols-2">
          <div>
            <div class="mb-5">
              <h3 class="text-2xl font-bold text-slate-900">
                Specialty Items & Debris
              </h3>
              <p class="mt-2 text-sm text-slate-600">
                Common item removals with clear starting prices.
              </p>
            </div>

            <div class="space-y-4">
              <div
                v-for="service in specialtyItems"
                :key="service.id"
                class="rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-primary-light"
              >
                <div
                  class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="min-w-0">
                    <h4 class="text-lg font-bold leading-snug text-slate-900">
                      {{ service.name }}
                    </h4>
                    <p class="mt-2 text-sm leading-relaxed text-slate-600">
                      {{ service.description }}
                    </p>
                    <p
                      v-if="service.note"
                      class="mt-3 text-xs font-medium leading-relaxed text-slate-500"
                    >
                      {{ service.note }}
                    </p>
                  </div>
                  <div
                    class="shrink-0 border-t border-slate-200 pt-3 sm:min-w-40 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0 sm:text-right"
                  >
                    <div
                      class="text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      Price
                    </div>
                    <div
                      class="mt-1 text-xl font-bold leading-tight text-primary-main"
                    >
                      {{ service.price }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="mb-5">
              <h3 class="text-2xl font-bold text-slate-900">
                Demolition Services
              </h3>
              <p class="mt-2 text-sm text-slate-600">
                Larger tear-down jobs are confirmed with an on-site estimate.
              </p>
            </div>

            <div class="space-y-4">
              <div
                v-for="service in demolitionServices"
                :key="service.id"
                class="rounded-xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-primary-light"
              >
                <div
                  class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div class="min-w-0">
                    <h4 class="text-lg font-bold leading-snug text-slate-900">
                      {{ service.name }}
                    </h4>
                    <p class="mt-2 text-sm leading-relaxed text-slate-600">
                      {{ service.description }}
                    </p>
                    <p
                      v-if="service.note"
                      class="mt-3 text-xs font-medium leading-relaxed text-slate-500"
                    >
                      {{ service.note }}
                    </p>
                  </div>
                  <div
                    class="shrink-0 border-t border-slate-200 pt-3 sm:min-w-40 sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0 sm:text-right"
                  >
                    <div
                      class="text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      Price
                    </div>
                    <div
                      class="mt-1 text-xl font-bold leading-tight text-primary-main"
                    >
                      {{ service.price }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How Pricing Works -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-6xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            How Our Pricing Works
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Simple, transparent process from estimate to completion
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">1</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Get an Estimate
            </h3>
            <p class="text-slate-600">
              Text us photos or book an on-site quote for accurate pricing
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">2</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              On-Site Confirmation
            </h3>
            <p class="text-slate-600">
              We confirm the final price after seeing your items in person
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">3</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Complete Service
            </h3>
            <p class="text-slate-600">
              Professional removal, cleanup, and responsible disposal
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing FAQ -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about our pricing and services
          </p>
        </div>

        <div
          class="mt-6 divide-y divide-slate-200 rounded-lg border max-w-4xl mx-auto"
        >
          <Faq q="How do you determine pricing?">
            Our pricing is volume-based, meaning we charge based on how much
            space your items take up in our truck. We provide on-site pricing
            confirmation to ensure accuracy.
          </Faq>
          <Faq q="Can I get an estimate before you arrive?">
            Yes! You can text us photos of your items for a rough estimate. For
            the most accurate pricing, we'll confirm the final amount on-site.
          </Faq>
          <Faq q="Are there any hidden fees?">
            No hidden fees! Our quoted price includes pickup, loading,
            transportation, and disposal. What we quote is what you pay.
          </Faq>
          <Faq q="Do you offer same-day service?">
            Yes, we often have same-day availability! It's best to call early in
            the day to secure a spot. Availability depends on our current
            schedule and your location.
          </Faq>
          <Faq q="What payment methods do you accept?">
            We accept cash, credit cards, Zelle, Venmo, and Cash App. Payment is
            due upon completion of the service.
          </Faq>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-4xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Get Started?
        </h2>
        <p class="text-xl text-white mb-8">
          Get your free estimate today. No obligation, transparent pricing,
          professional service.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            @click="trackConversion"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call for Estimate
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
